import { useEffect, useMemo, useRef } from 'react';
import { cameFrom, track } from '../lib/growthTracker';

// Growth OS (step 7) — one service form's journey: started (first field with a
// value), step, field errors (the FIELD NAME only, never what was typed), fee
// shown, submitted, and abandoned (left after starting, without submitting).
// `service` is 'rfq' | 'managed' | 'idea' | 'factory'. Fire-and-forget.
export default function useServiceTracking(service) {
  const st = useRef({ started: 0, submitted: false, step: null, stepAt: 0, feeShown: false, feePending: false, service });
  st.current.service = service;

  useEffect(() => () => {
    const s = st.current;
    if (s.started && !s.submitted) {
      track('service_form_abandoned', { service: s.service, last_step: s.step || 'form', seconds_spent: Math.round((Date.now() - s.started) / 1000) });
    }
  }, []);

  return useMemo(() => ({
    start() {
      const s = st.current;
      if (s.started) return;
      s.started = Date.now(); s.stepAt = s.started;
      track('service_form_started', { service: s.service, entry_point: (cameFrom() || 'direct').slice(0, 40) });
      if (s.feePending) { s.feePending = false; s.feeShown = true; track('service_fee_shown', { service: s.service }); }
    },
    step(n, name) {
      const s = st.current;
      if (s.step === name) return;
      const now = Date.now();
      track('service_form_step', { service: s.service, step: n, step_name: name, seconds_on_step: Math.round((now - (s.stepAt || now)) / 1000) });
      s.step = name; s.stepAt = now;
    },
    fieldError(field) { track('service_form_field_error', { service: st.current.service, field }); },
    // The fee notice is often on screen at page load. It counts as seen by
    // someone who STARTED the form — else page views alone would push
    // «رأى الرسوم» above «بدأ النموذج» in the funnel (seen live 2026-11-01).
    feeShown() {
      const s = st.current;
      if (s.feeShown) return;
      if (!s.started) { s.feePending = true; return; }
      s.feeShown = true;
      track('service_fee_shown', { service: s.service });
    },
    submitted(props = {}) {
      const s = st.current;
      s.submitted = true;
      track('service_submitted', { service: s.service, ...props });
    },
  }), []);
}

// True once any field of a form state object holds a value — the moment a form counts as started.
export const anyFilled = (form, ignore = []) => Object.entries(form || {})
  .some(([k, v]) => !ignore.includes(k) && v != null && String(v).trim() !== '');
